import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { Loader2, Mail, MessageSquare, Plus, StickyNote } from 'lucide-react';
import { commsApi } from './phase567Api';
import { api } from './api';
import { GhlBadge } from './ContactTasks';
import { MessageList } from './MessageItem';
import MessageComposer from './MessageComposer';
import { blockText } from './sendStatus';
import { formatDateTime } from './kit/time';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';

// Ticket 188 D7: one chronological stream with channel chips. Calls and
// voicemails share a chip since both are GHL call events.
const CHIPS = [
  { key: 'all', label: 'All', channels: null },
  { key: 'sms', label: 'Texts', channels: ['sms'] },
  { key: 'email', label: 'Emails', channels: ['email'] },
  { key: 'calls', label: 'Calls', channels: ['call', 'voicemail'] },
];
const PAGE = 100;

// Ticket 162: oldest at the top, newest at the bottom, like every messaging
// app. The list lives in a capped scroll box that opens at the bottom and
// follows a new send.
const THREAD_MAX_H = 'max-h-[60vh]';

const byTime = (a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0);

// The conversation a channel's composer replies on: the most recently active
// text conversation of that channel.
function composerConvs(convs) {
  return ['sms', 'email'].map((channel) => convs
    .filter((c) => c.channel === channel)
    .sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0))[0])
    .filter(Boolean);
}

// Contact profile -> Communications tab: every message for this contact in one
// stream, oldest first (newest at the bottom, ticket 162), filtered by channel
// chip. Reuses MessageItem so bubbles/call cards match the Inbox exactly.
function Conversations({ contactId, status, onStatusStale }) {
  const [state, setState] = useState({ loading: true, error: null, items: [], convs: [], nextBefore: null });
  const [transports, setTransports] = useState({ sms: 'stub', email: 'stub' });
  const [chip, setChip] = useState('all');
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [olderError, setOlderError] = useState('');
  const boxRef = useRef(null);
  // Distance from the bottom to restore after older messages are prepended;
  // null means "pin to the bottom" (first load, a new send, a chip change).
  const anchor = useRef(null);

  useEffect(() => {
    commsApi.config().then(setTransports).catch(() => {});
  }, []);

  const onSent = (m) => setState((s) => ({ ...s, items: [...s.items, m] }));

  useEffect(() => {
    let alive = true;
    setState({ loading: true, error: null, items: [], convs: [], nextBefore: null });
    setChip('all');
    setOlderError('');
    anchor.current = null;
    commsApi.contactMessages(contactId, { limit: PAGE })
      .then((d) => {
        if (!alive) return;
        setState({
          loading: false, error: null, items: [...(d.items || [])].sort(byTime),
          convs: d.conversations || [], nextBefore: d.next_before || null,
        });
      })
      .catch((e) => {
        if (!alive) return;
        setState({ loading: false, error: e?.response?.data?.detail || 'Failed to load conversations', items: [], convs: [], nextBefore: null });
      });
    return () => { alive = false; };
  }, [contactId]);

  const current = useRef(contactId);
  current.current = contactId;

  const loadOlder = async () => {
    const forContact = contactId;
    const box = boxRef.current;
    setLoadingOlder(true);
    setOlderError('');
    try {
      const d = await commsApi.contactMessages(forContact, { limit: PAGE, before: state.nextBefore });
      if (current.current !== forContact) return;
      if (box) anchor.current = box.scrollHeight - box.scrollTop;
      setState((s) => {
        const have = new Set(s.items.map((m) => m.message_id));
        const older = (d.items || []).filter((m) => !have.has(m.message_id));
        return { ...s, items: [...older, ...s.items].sort(byTime), nextBefore: d.next_before || null };
      });
    } catch (e) {
      if (current.current === forContact) setOlderError(e?.response?.data?.detail || 'Could not load older messages');
    } finally {
      setLoadingOlder(false);
    }
  };

  // Ticket 141: open (or reopen — the server keeps one thread per contact
  // and channel) a conversation and scroll to its composer.
  const [starting, setStarting] = useState(null);
  const [startError, setStartError] = useState('');
  const start = async (channel) => {
    const forContact = contactId;
    setStarting(channel);
    setStartError('');
    try {
      const conv = await commsApi.createConversation(forContact, channel);
      if (current.current !== forContact) return;  // moved to another contact
      setState((s) => (s.convs.some((c) => c.conversation_id === conv.conversation_id) ? s : {
        ...s,
        convs: [conv, ...s.convs],
      }));
      requestAnimationFrame(() => {
        document.querySelector(`[data-testid="contact-comms-conv-${conv.conversation_id}"]`)
          ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      });
    } catch (e) {
      setStartError(e?.response?.data?.detail || 'Could not start the conversation');
    } finally { setStarting(null); }
  };

  const { loading, error, items, convs, nextBefore } = state;
  const channels = CHIPS.find((c) => c.key === chip)?.channels;
  const visible = channels ? items.filter((m) => channels.includes(m.channel)) : items;

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return;
    box.scrollTop = anchor.current == null ? box.scrollHeight : box.scrollHeight - anchor.current;
    anchor.current = null;
  }, [visible.length, items.length, chip, loading]);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400" data-testid="contact-comms-loading">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading conversations...
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 border border-red-100 rounded-2xl p-8 text-center text-red-600 text-sm" data-testid="contact-comms-error">
        {error}
      </div>
    );
  }
  const startBar = (
    <StartBar transports={transports} status={status} starting={starting} error={startError} onStart={start} />
  );
  if (convs.length === 0 && items.length === 0) {
    return (
      <div className="space-y-4">
        {startBar}
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-12 text-center text-slate-400" data-testid="contact-comms-empty">
          <p className="text-sm">No conversations yet.</p>
        </div>
      </div>
    );
  }
  const countOf = (c) => (c.channels ? items.filter((m) => c.channels.includes(m.channel)).length : items.length);

  return (
    <div className="space-y-4" data-testid="contact-comms">
      {startBar}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3 flex-wrap" role="group" aria-label="Filter by channel">
          {CHIPS.map((c) => (
            <button key={c.key} type="button" onClick={() => { anchor.current = null; setChip(c.key); }}
              aria-pressed={chip === c.key} data-testid={`comms-chip-${c.key}`}
              className={`px-3 py-1 rounded-full text-xs border ${chip === c.key
                ? 'bg-slate-900 text-white border-slate-900'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}>
              {c.label} <span className="opacity-70" data-testid={`comms-chip-count-${c.key}`}>{countOf(c)}</span>
            </button>
          ))}
        </div>
        <div ref={boxRef} className={`space-y-2 overflow-y-auto ${THREAD_MAX_H}`} data-testid="contact-comms-messages">
          {nextBefore && (
            <div className="text-center">
              <button type="button" onClick={loadOlder} disabled={loadingOlder} data-testid="comms-load-older"
                className="text-xs px-3 py-1 rounded-full border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60">
                {loadingOlder ? 'Loading...' : 'Load older'}
              </button>
              {olderError && <p className="text-xs text-red-600 mt-1" role="alert">{olderError}</p>}
            </div>
          )}
          {visible.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-6" data-testid="comms-filter-empty">
              No messages in this view yet.
            </p>
          )}
          {/* Ticket 164: same side-by-side layout as the Inbox thread, with day pills. */}
          <MessageList messages={visible} contactId={contactId} contactName={convs[0]?.contact_snapshot?.display_name} />
        </div>
        {/* Ticket 135 S2: same composer as the Inbox, one per text channel. */}
        {composerConvs(convs).map((conv, i, all) => (
          <div key={conv.conversation_id} className={`-mx-4 mt-3${i === all.length - 1 ? ' -mb-4' : ''}`}
            data-testid={`contact-comms-conv-${conv.conversation_id}`}>
            <MessageComposer conv={conv} transports={transports} status={status}
              onSent={onSent} onBlocked={onStatusStale} />
          </div>
        ))}
      </div>
    </div>
  );
}

const START = [
  { channel: 'sms', label: 'New text', icon: MessageSquare },
  { channel: 'email', label: 'New email', icon: Mail },
];

// One button per channel whose transport is on. A blocked channel stays
// visible but disabled, with the reason as its tooltip and under the bar.
function StartBar({ transports, status, starting, error, onStart }) {
  const idBase = useId();
  const channels = START.filter((b) => transports?.[b.channel] !== 'off');
  if (channels.length === 0) return null;
  const reasons = channels
    .map((b) => [b, blockText(status, b.channel)])
    .filter(([, why]) => why);
  return (
    <div data-testid="contact-comms-start">
      <div className="flex items-center gap-2 flex-wrap">
        {channels.map(({ channel, label, icon: Icon }) => {
          const why = blockText(status, channel);
          return (
            <button key={channel} onClick={() => onStart(channel)} disabled={!!why || !!starting}
              title={why || undefined} aria-describedby={why ? `${idBase}-${channel}` : undefined}
              data-testid={`start-${channel}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed">
              {starting === channel ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
              <Icon size={14} /> {label}
            </button>
          );
        })}
      </div>
      {reasons.map(([b, why]) => (
        <p key={b.channel} id={`${idBase}-${b.channel}`} className="text-xs text-amber-700 mt-2"
          data-testid={`start-${b.channel}-blocked`}>
          {b.channel === 'sms' ? 'Texting' : 'Email'} is blocked: {why}.
        </p>
      ))}
      {error && <p className="text-xs text-red-600 mt-2" role="alert">{error}</p>}
    </div>
  );
}

// Ticket 231: add a note. On a GHL contact with write-through on
// (`writesToGhl`), the server writes it to GoHighLevel first; a GHL refusal
// keeps the text in the box and says why.
function AddNote({ contactId, writesToGhl, onAdded }) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post('/notes', { contact_id: contactId, content: text.trim() });
      toast({ title: data?.source === 'ghl' ? 'Note added in GoHighLevel' : 'Note added' });
      setText('');
      onAdded();
    } catch (e) {
      toast({ title: 'Could not add the note', description: errorMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-3" data-testid="note-add">
      <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a note"
        aria-label="New note" data-testid="note-add-input" />
      <div className="flex items-center justify-between gap-2 mt-2">
        <span className="text-xs text-slate-400">{writesToGhl ? 'Saved to GoHighLevel too' : ''}</span>
        <Button variant="accent" size="sm" onClick={save} disabled={saving || !text.trim()} data-testid="note-add-save">
          <Plus size={14} /> Add note
        </Button>
      </div>
    </div>
  );
}

// Ticket 135 S3: notes (BFY and GHL-mirrored) inline under the thread,
// newest first. Shown even when the contact has no conversations.
function ContactNotes({ contactId, writesToGhl }) {
  const [notes, setNotes] = useState([]);

  const load = useCallback(() => api.get('/notes', { params: { contact_id: contactId } })
    .then(({ data }) => (Array.isArray(data) ? data : []))
    .catch(() => []), [contactId]);

  useEffect(() => {
    let alive = true;
    load().then((list) => { if (alive) setNotes(list); });
    return () => { alive = false; };
  }, [load]);

  const sorted = [...notes].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  return (
    <div data-testid="contact-comms-notes">
      <div className="flex items-center gap-2 mb-3 text-sm font-medium text-slate-500">
        <StickyNote size={14} /> Notes
      </div>
      <AddNote contactId={contactId} writesToGhl={writesToGhl} onAdded={() => load().then(setNotes)} />
      <div className="space-y-3">
        {sorted.map((n) => (
          <div key={n.note_id} className="bg-amber-50/60 rounded-2xl border border-amber-100 p-4" data-testid={`contact-note-${n.note_id}`}>
            <div className="flex items-center gap-2 mb-1.5 flex-wrap text-xs text-slate-500">
              <span>{n.created_at ? formatDateTime(n.created_at) : ''}</span>
              {n.owner_name && <span>· {n.owner_name}</span>}
              {n.source === 'ghl' && <GhlBadge testId={`ghl-badge-${n.note_id}`} />}
            </div>
            <div className="text-sm text-slate-700 whitespace-pre-wrap break-words">{n.content}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ContactCommunications({ contactId, status, onStatusStale, notesToGhl = false }) {
  return (
    <div className="space-y-8">
      <Conversations contactId={contactId} status={status} onStatusStale={onStatusStale} />
      <ContactNotes contactId={contactId} writesToGhl={notesToGhl} />
    </div>
  );
}
